import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Config from 'config/config';
import { isNil, pick, remove } from 'lodash';
import { concatAll, defer, from, of, switchMap } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CompanyForm } from 'src/app/classes/form-groups/company-form';
import { LoadingMask } from 'src/app/classes/loading-mask';
import {
  Address,
  Company,
  CompanyOperationState,
  ESignProvider,
  Note,
  SubNationalDivision
} from 'src/app/classes/models';
import { S3PresignedUrl } from 'src/app/classes/s3-presigned-url';
import { CompanyStructures } from 'src/app/interfaces/company-structures';
import { UserAccountType } from 'src/app/interfaces/models/user-account-type';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { GetUserInputModalComponent } from 'src/app/modals/get-user-input-modal/get-user-input-modal.component';
import { LogoFileUploadModalComponent } from 'src/app/modals/logo-file-upload-modal/logo-file-upload-modal.component';
import { SelectComplianceProgramModalComponent } from 'src/app/modals/select-compliance-program-modal/select-compliance-program-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { ESignService } from 'src/app/services/esign.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-add-edit-client',
  templateUrl: './add-edit-client.component.html',
  styleUrls: ['./add-edit-client.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditClientComponent implements OnInit {
  constructor(
    private AuthService: AuthService,
    private CompanyService: CompanyService,
    private route: ActivatedRoute,
    private Router: Router,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private ModalService: NgbModal,
    private SubNationalDivisionService: SubNationalDivisionService,
    private ESignService: ESignService,
    private HostElement: ElementRef
  ) {
    const paramMap = this.route.snapshot.paramMap;
    const CompanyId = Number(paramMap.get('CompanyId'));
    const { UserAccountType } = this.LoggedInUser;

    this.userAccountType = UserAccountType;

    this.ESignService.getESignProviders(
      this.UtilityService.stringifyObjectsInQueryObject({
        name: { $in: ['RSign', 'DocuSign', 'VIVE'] }
      })
    ).subscribe({
      next: (ESignProviders) => {
        this.ESignProviders.push(...ESignProviders);
      }
    });

    if (CompanyId) {
      this.loadData(CompanyId);
    } else {
      this.company = new Company({});

      this.listenToCompanyChanges();
      this.listenToMailingAddressChanges();
    }

    // Not used on this component yet
    this.companyForm.removeControl('CompanyPackagePricing');
  }

  company?: Company;
  companyId?: number;
  companyForm: CompanyForm = new CompanyForm();
  companyStructures: CompanyStructures[] = [
    'Corporation',
    'Sole Proprietorship',
    'PartnerShip'
  ];
  companyOperationStates: CompanyOperationState[] = [];
  subNationalDivisions: SubNationalDivision[] = [];
  countriesMap: Map<string, string> = new Map([
    ['US', 'United States'],
    ['CA', 'Canada']
  ]);
  packageTypes: Company['packageType'][] = ['BASE', 'FULL'];
  packageType: 'FULL' | 'BASE' | null = null;
  LoggedInUser = this.AuthService.getUser();
  logoUrl: string | null = null;
  logoLoading = true;

  companyLinksMap: Map<
    string,
    {
      link: string | null;
      formControl?: FormControl;
      copyURL?: boolean;
      updateURL?: boolean;
      openInNewTab?: boolean;
    }
  > = new Map([]);

  phoneMask = this.UtilityService.phoneMask;
  einMask = this.UtilityService.einMask;
  isMailingAndStreetSame: boolean = false;
  userAccountType?: UserAccountType;
  operationStatesDropdownOpen: boolean = false;

  ESignProviders: ESignProvider[] = [];
  yesNoOptions: { label: string; value: boolean | null }[] = [
    { label: 'Not Set', value: null },
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  contactTypes: string[] = [
    'Client Vendor Contact',
    'VIVE Champion',
    'Vendor Agreement Contact',
    'Waiver Contact'
  ];

  get contactsList() {
    return this.companyForm.controls.contacts.value || [];
  }

  addContact() {
    this.companyForm.controls.contacts.patchValue([
      ...this.contactsList,
      { type: '', name: '', phone: '', email: '' }
    ]);
  }

  removeContact(index: number) {
    const list = this.contactsList.filter((_, i) => i !== index);
    this.companyForm.controls.contacts.patchValue(list.length ? list : null);
  }

  updateContactField(
    index: number,
    field: 'type' | 'name' | 'phone' | 'email',
    val: string
  ) {
    const list = this.contactsList.map((c, i) =>
      i === index ? { ...c, [field]: val } : c
    );
    this.companyForm.controls.contacts.patchValue(list);
  }

  @Input() showCancelButton: boolean = false;
  @Output() saved = new EventEmitter<Company>();
  @Output() cancelled = new EventEmitter<void>();
  internalNotes: Note[] = [];
  newInternalNote: string = '';
  isInternalNotesLoading: boolean = false;
  isPostingInternalNote: boolean = false;
  internalNoteIdsBeingDeleted: Set<number> = new Set();
  lastLoadedSubNationalDivisionCountry: 'US' | 'CA' | null = null;
  activeClientInfoTab: 'companyInfo' | 'clientListData' | 'lifecycleDates' =
    'companyInfo';

  get showClientDataTab() {
    return this.LoggedInUser.UserAccountType?.name !== 'Client';
  }

  get isCompanyInfoTabActive() {
    return (
      !this.showClientDataTab || this.activeClientInfoTab === 'companyInfo'
    );
  }

  get isClientListDataTabActive() {
    return (
      this.showClientDataTab && this.activeClientInfoTab === 'clientListData'
    );
  }

  get isLifecycleDatesTabActive() {
    return (
      this.showClientDataTab && this.activeClientInfoTab === 'lifecycleDates'
    );
  }

  get internalNotesCompanyId() {
    return this.companyId || this.company?.id;
  }

  setActiveClientInfoTab(
    tab: 'companyInfo' | 'clientListData' | 'lifecycleDates'
  ) {
    this.activeClientInfoTab = tab;
  }

  loadData(CompanyId: number) {
    this.companyId = CompanyId;

    const companyQuery = {
      StreetAddress: {},
      MailingAddress: {},
      CompanyOperationState: {
        SubNationalDivision: {}
      },
      CompanyComplianceType: {
        attributes: ['id', 'type']
      },
      LogoFile: {}
    };

    this.CompanyService.getCompany(
      this.companyId,
      this.UtilityService.stringifyObjectsInQueryObject(companyQuery)
    ).subscribe({
      next: (Company: Company) => {
        this.company = Company;
        const { logo: _logo, ...companyWithoutLogo } = this.company as any;
        this.companyForm.patchValue(companyWithoutLogo);

        if (!this.company) {
          throw this.ToastService.addErrorToast('Error', 'No Company Found.', {
            returnError: true
          });
        }

        if (this.userAccountType?.name === 'Client') {
          // Don't want clients editing their company details
          this.companyForm.disable();
        }

        const {
          MailingAddress,
          StreetAddress,
          fullServiceOnsite,
          fullServiceOffsite,
          CompanyOperationStates,
          country,
          LogoFile
        } = this.company;

        if (LogoFile) {
          this.CompanyService.getCompanyLogoS3PresignedUrl(
            this.company.id!
          ).subscribe({
            next: (s3Response: S3PresignedUrl) => {
              this.logoUrl = s3Response.presignedUrl;
              this.logoLoading = false;
            }
          });
        } else {
          this.logoLoading = false;
        }

        this.packageType =
          fullServiceOnsite && fullServiceOffsite ? 'FULL' : 'BASE';

        if (CompanyOperationStates) {
          this.companyOperationStates = CompanyOperationStates;

          this.updateCompanyOperationStates();
        }

        if (country) {
          this.setSubNationalDivisions(country);
        }

        if (MailingAddress && StreetAddress) {
          const fieldsToCheck: (keyof Address)[] = [
            'address1',
            'address2',
            'city',
            'country',
            'zip',
            'state'
          ];

          const addressesAreSame = fieldsToCheck.every((field) => {
            const mailingAddressValue = MailingAddress[field];
            const streetAddressValue = StreetAddress[field];
            const isMailingAddressEmpty =
              mailingAddressValue === null || mailingAddressValue === '';
            const isStreetAddressEmpty =
              streetAddressValue === null || streetAddressValue === '';

            if (isMailingAddressEmpty && isStreetAddressEmpty) {
              return true;
            } else if (!isMailingAddressEmpty && !isStreetAddressEmpty) {
              return mailingAddressValue === streetAddressValue;
            } else {
              return false;
            }
          });

          if (addressesAreSame) {
            this.onIsMailingAndStreetSameChange();
          }
        }

        this.companyLinksMap.set('Exhibit A URL:', {
          link: this.companyForm.controls.exibitA.value,
          formControl: this.companyForm.controls.exibitA,
          copyURL: true,
          updateURL: true
        });
        this.companyLinksMap.set('Vendor Sign-up link:', {
          link: (() => {
            return `${this.getVendorPortalOrigin()}/vendor-signup/${this.companyForm.controls.vendorSignUpCode.value}/company/${this.company.id}/complianceType/default/home`;
          })(),
          copyURL: true,
          openInNewTab: true
        });

        this.listenToCompanyChanges();
        this.listenToMailingAddressChanges();
        if (this.showClientDataTab) {
          this.loadInternalNotes();
        }
      }
    });
  }

  loadInternalNotes() {
    const CompanyId = this.internalNotesCompanyId;

    if (!CompanyId) {
      this.internalNotes = [];
      return;
    }

    this.isInternalNotesLoading = true;

    this.CompanyService.getCompanyInternalNotes(CompanyId).subscribe({
      next: (notes) => {
        this.internalNotes = notes;
        this.isInternalNotesLoading = false;
      },
      error: () => {
        this.isInternalNotesLoading = false;
      }
    });
  }

  getInternalNoteAuthor(note: Note) {
    const firstName = note.User?.firstName || '';
    const lastName = note.User?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    if (fullName) {
      return fullName;
    }

    if (note.User?.email) {
      return note.User.email;
    }

    return 'Unknown User';
  }

  addInternalNote() {
    const noteText = this.newInternalNote.trim();
    const CompanyId = this.internalNotesCompanyId;

    if (!CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Save client before posting internal notes.',
        { returnError: true }
      );
    }

    if (!noteText) {
      return;
    }

    this.isPostingInternalNote = true;

    this.CompanyService.saveCompanyInternalNote(CompanyId, noteText).subscribe({
      next: (savedNote) => {
        const SavedNote = new Note(savedNote || {});

        if (!SavedNote.User && this.LoggedInUser) {
          Object.assign(SavedNote, {
            User: pick(this.LoggedInUser, [
              'id',
              'firstName',
              'lastName',
              'email'
            ])
          });
        }

        this.internalNotes = [
          SavedNote,
          ...this.internalNotes.filter((note) => note.id !== SavedNote.id)
        ];
        this.newInternalNote = '';
        this.isPostingInternalNote = false;
        this.ToastService.addSuccessToast(
          'Success',
          'Internal note posted successfully.'
        );
      },
      error: (error) => {
        this.isPostingInternalNote = false;
        this.ToastService.addErrorToast(
          'Error',
          error?.error?.message || 'Unable to save internal note.'
        );
      }
    });
  }

  isDeletingInternalNote(note: Note) {
    return !!note.id && this.internalNoteIdsBeingDeleted.has(note.id);
  }

  deleteInternalNote(note: Note) {
    const CompanyId = this.internalNotesCompanyId;
    const NoteId = note.id;

    if (!CompanyId || !NoteId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot delete internal note.',
        { returnError: true }
      );
    }

    if (!window.confirm('Delete this internal note?')) {
      return;
    }

    this.internalNoteIdsBeingDeleted.add(NoteId);

    this.CompanyService.deleteCompanyInternalNote(CompanyId, NoteId).subscribe({
      next: () => {
        this.internalNotes = this.internalNotes.filter((n) => n.id !== NoteId);
        this.internalNoteIdsBeingDeleted.delete(NoteId);
        this.ToastService.addSuccessToast(
          'Success',
          'Internal note deleted successfully.'
        );
      },
      error: (error) => {
        this.internalNoteIdsBeingDeleted.delete(NoteId);
        this.ToastService.addErrorToast(
          'Error',
          error?.error?.message || 'Unable to delete internal note.'
        );
      }
    });
  }

  setSubNationalDivisions(country?: 'US' | 'CA') {
    if (!country) {
      this.subNationalDivisions = [];
      this.lastLoadedSubNationalDivisionCountry = null;
      return;
    }

    this.SubNationalDivisionService.getSubNationalDivisions({
      country
    }).subscribe({
      next: (SubNationalDivisions: SubNationalDivision[]) => {
        this.subNationalDivisions = SubNationalDivisions;
        this.lastLoadedSubNationalDivisionCountry = country;
      },
      error: () => {
        this.subNationalDivisions = [];
      }
    });
  }

  updateCompanyOperationStates() {
    if (!this.company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot update CompanyOperationStates. No company found.',
        { returnError: true }
      );
    }

    Object.assign(this.company, {
      CompanyOperationStates: this.companyOperationStates
    });

    this.companyOperationStates.sort((a, b) => {
      const abbreviationA = a.SubNationalDivision?.abbreviation || '';
      const abbreviationB = b.SubNationalDivision?.abbreviation || '';
      return abbreviationA.localeCompare(abbreviationB);
    });
  }

  selectOption(option: SubNationalDivision) {
    const newCompanyOperationState: CompanyOperationState = {
      CompanyId: this.companyId,
      SubNationalDivision: option,
      SubNationalDivisionId: option.id
    };

    if (
      !this.companyOperationStates.find(
        (cos) =>
          cos.SubNationalDivisionId ===
          newCompanyOperationState.SubNationalDivisionId
      )
    ) {
      this.companyOperationStates.push(newCompanyOperationState);
    }

    this.updateCompanyOperationStates();
  }

  deselectOption(option: CompanyOperationState) {
    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { centered: true, size: 'md' }
    );

    Object.assign(componentInstance, {
      displayText: `Are you sure you want to remove ${option.SubNationalDivision?.abbreviation} as an operating state?`
    });

    from(result).subscribe({
      next: (confirmed) => {
        if (confirmed) {
          remove(this.companyOperationStates, (o) => o === option);
          this.updateCompanyOperationStates();
        }
      }
    });
  }

  toggleOperationStatesDropdown() {
    this.operationStatesDropdownOpen = !this.operationStatesDropdownOpen;
  }

  get availableOperationStates(): SubNationalDivision[] {
    const selectedIds = this.companyOperationStates.map(
      (cos) => cos.SubNationalDivisionId
    );
    return this.subNationalDivisions.filter(
      (division) => !selectedIds.includes(division.id)
    );
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: Event) {
    const target = event.target as HTMLElement;
    const dropdown = this.HostElement.nativeElement.querySelector(
      '#operation-states-dropdown'
    );

    if (dropdown && !dropdown.contains(target)) {
      this.operationStatesDropdownOpen = false;
    }
  }

  listenToMailingAddressChanges() {
    const mailingAddress = this.companyForm.controls.MailingAddress;
    const streetAddress = this.companyForm.controls.StreetAddress;

    mailingAddress.valueChanges.subscribe({
      next: (changes: Address) => {
        if (this.isMailingAndStreetSame) {
          const patchValues = pick(changes, [
            'address1',
            'address2',
            'city',
            'state',
            'zip'
          ]);

          streetAddress.patchValue(patchValues);
        }
      }
    });
  }

  listenToCompanyChanges() {
    if (!this.company) {
      throw this.ToastService.addErrorToast('Error', 'No Company Found.', {
        returnError: true
      });
    }

    this.companyForm.valueChanges.pipe(debounceTime(400)).subscribe({
      next: (changes) => {
        const { country } = changes;

        if (
          !isNil(country) &&
          country !== this.lastLoadedSubNationalDivisionCountry
        ) {
          this.setSubNationalDivisions(country);
        }

        Object.assign(this.company!, changes);
      }
    });
  }

  onPackageTypeChange() {
    if (this.packageType === 'FULL') {
      this.companyForm.controls.fullServiceOnsite.patchValue(true);
      this.companyForm.controls.fullServiceOffsite.patchValue(true);
    }

    if (this.packageType === 'BASE') {
      this.companyForm.controls.fullServiceOnsite.patchValue(false);
      this.companyForm.controls.fullServiceOffsite.patchValue(false);
    }
  }

  onIsMailingAndStreetSameChange() {
    const mailingAddress = this.companyForm.controls.MailingAddress;
    const streetAddress = this.companyForm.controls.StreetAddress;

    this.isMailingAndStreetSame = !this.isMailingAndStreetSame;

    if (this.isMailingAndStreetSame && streetAddress) {
      streetAddress.disable();
      streetAddress.patchValue(
        pick(mailingAddress.getRawValue(), [
          'address1',
          'address2',
          'city',
          'state',
          'zip',
          'latitude',
          'longitude'
        ])
      );
    } else if (!this.isMailingAndStreetSame && streetAddress) {
      streetAddress.enable();
      streetAddress.patchValue({
        address1: undefined,
        address2: undefined,
        city: undefined,
        state: undefined,
        zip: undefined
      });
    }
  }

  selectComplianceProgram() {
    const { componentInstance, closed } = this.ModalService.open(
      SelectComplianceProgramModalComponent,
      { centered: true, size: 'lg', backdrop: 'static' }
    );

    Object.assign(componentInstance, {
      CompanyId: this.company?.id
    });

    return closed;
  }

  /**
   * Origin for vendor sign-up links. These must open in the vendor portal
   * (vive-vendors), not this legacy staff app, so we use `vendorsAppUrl` from
   * config. Falls back to the current window origin if `vendorsAppUrl` is not
   * configured so the copy button can never emit an `undefined/...` URL.
   */
  private getVendorPortalOrigin(): string {
    const { vendorsAppUrl } = Config.getEnvironmentConfig();
    return (
      vendorsAppUrl ?? `${window.location.protocol}//${window.location.host}`
    );
  }

  setCompanyComplianceType() {
    return this.selectComplianceProgram().pipe(
      switchMap((complianceTypeId: number) => {
        if (!complianceTypeId) {
          return of(null);
        }

        const selectedComplianceType =
          this.company?.CompanyComplianceTypes?.find(
            (cct) => cct.id === complianceTypeId
          );

        if (!selectedComplianceType) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Compliance Type found.',
            { returnError: true }
          );
        }

        this.companyLinksMap.set('Vendor Sign-up link:', {
          link: `${this.getVendorPortalOrigin()}/vendor-signup/${this.companyForm.controls.vendorSignUpCode.value}/company/${this.company?.id}/complianceType/${selectedComplianceType.type}/home`,
          copyURL: true,
          openInNewTab: true
        });

        return of(true);
      })
    );
  }

  copyUrl(entry: {
    link: string | null;
    formControl?: FormControl;
    openInNewTab?: boolean;
  }) {
    // For entries with a formControl (e.g., Exhibit A URL), copy the current value directly
    if (entry.formControl) {
      const url = entry.formControl.value;
      if (!url) {
        this.ToastService.addErrorToast('Error', 'No URL to copy.');
        return;
      }
      navigator.clipboard
        .writeText(url)
        .then(() =>
          this.ToastService.addSuccessToast(
            'Success',
            'Copied URL to clipboard.'
          )
        )
        .catch((err) =>
          this.ToastService.addErrorToast('Error', err, {
            returnError: true
          })
        );
      return;
    }

    // For sign-up links, handle compliance type selection first
    const observables = [];

    if (
      this.company?.CompanyComplianceTypes &&
      this.company?.CompanyComplianceTypes.length > 1
    ) {
      observables.push(defer(() => this.setCompanyComplianceType()));
    }

    observables.push(
      defer(() =>
        from(
          navigator.clipboard
            .writeText(
              this.companyLinksMap.get('Vendor Sign-up link:')?.link || ''
            )
            .then(() =>
              this.ToastService.addSuccessToast(
                'Success',
                'Copied URL to clipboard.'
              )
            )
            .catch((err) =>
              this.ToastService.addErrorToast('Error', err, {
                returnError: true
              })
            )
        )
      )
    );

    from(observables).pipe(concatAll()).subscribe();
  }

  updateUrl(formControl?: FormControl) {
    if (!formControl) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot update URL. No formControl found.'
      );
    }

    const modalRef = this.ModalService.open(GetUserInputModalComponent, {
      centered: true,
      size: 'lg'
    });
    modalRef.componentInstance.title = 'Update URL';
    modalRef.componentInstance.message = 'Please update url.';
    modalRef.componentInstance.inputValue = formControl.value;

    modalRef.closed.subscribe({
      next: (updatedUrl: string) => {
        if (this.companyForm) {
          formControl.patchValue(updatedUrl);
        }

        if (this.company && this.company.id) {
          this.save();
        }
      }
    });
  }

  openLinkInNewTab(link: string | null) {
    if (!link) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot open link in new tab. No link found.'
      );
    }

    const observables = [];

    if (
      this.company?.CompanyComplianceTypes &&
      this.company?.CompanyComplianceTypes.length > 1
    ) {
      observables.push(defer(() => this.setCompanyComplianceType()));
    }

    observables.push(
      defer(() =>
        of(
          window.open(
            this.companyLinksMap.get('Vendor Sign-up link:')?.link || '',
            '_blank'
          )
        )
      )
    );

    from(observables).pipe(concatAll()).subscribe();
  }

  updateLogo() {
    const modalRef = this.ModalService.open(LogoFileUploadModalComponent, {
      centered: true,
      size: 'lg'
    });
    modalRef.closed.subscribe({
      next: (FileString) => {
        this.companyForm.controls['logo'].patchValue(FileString);
        if (this.company && this.company.id) {
          this.save();
        }
      }
    });
  }

  fieldsToRemoveOnSave() {
    // Not sure if we want to set some of these on client creation?
    const fieldsToRemove = [
      'vendorSignUpCode',
      'feeWaiverAllowedAmt',
      'launchWindow',
      'registrationStatus',
      'status',
      'companyType'
    ];

    fieldsToRemove.forEach((field) => {
      if (this.companyForm.get(field)) {
        this.companyForm.removeControl(field);
      }

      if (this.company && field in this.company) {
        delete this.company[field as keyof Company];
      }
    });
  }

  save() {
    if (!this.company || !this.companyForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save company info.',
        { returnError: true }
      );
    }

    if (this.companyOperationStates.length === 0) {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true, size: 'md' }
      );

      Object.assign(componentInstance, {
        displayText:
          'No operating states are selected. Are you sure you want to save?'
      });

      from(result).subscribe({
        next: (confirmed) => {
          if (confirmed) {
            this.executeSave();
          }
        }
      });

      return;
    }

    this.executeSave();
  }

  private executeSave() {
    if (!this.company || !this.companyForm) {
      return;
    }

    this.fieldsToRemoveOnSave();

    const validationErrorObj =
      this.UtilityService.generateValidationErrorsObject(this.companyForm);

    if (validationErrorObj) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There missing items in this form.',
        { returnError: true }
      );
    }

    const { nativeElement } = this.HostElement;
    const mask = new LoadingMask(nativeElement, {
      maskText: 'Saving Client...'
    });

    const wasNewCompany = !this.company.id;

    Object.assign(this.company, this.companyForm.getRawValue());

    this.CompanyService.saveCompany(this.company).subscribe({
      next: (Company: Company) => {
        if (!Company || !Company.id) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Company was not created correctly',
            { returnError: true }
          );
        }

        mask.destroy();
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully saved client information.'
        );

        this.company = Company;
        this.companyId = Company.id;

        if (Company.LogoFile) {
          this.logoUrl = null;
          this.CompanyService.getCompanyLogoS3PresignedUrl(
            Company.id!
          ).subscribe({
            next: (s3Response: S3PresignedUrl) => {
              this.logoUrl = `${s3Response.presignedUrl}&cb=${Date.now()}`;
            }
          });
        }

        if (wasNewCompany) {
          this.Router.navigate(['admin', 'client', Company.id, 'clientInfo']);
        }

        this.saved.emit(Company);
      },
      error: (err) => {
        mask.destroy();
      }
    });
  }

  cancelEdit() {
    this.cancelled.emit();
  }

  hasValidEditPermission() {
    if (!this.LoggedInUser) {
      throw this.ToastService.addErrorToast('Error', 'Not logged in.', {
        returnError: true
      });
    }

    const { UserAccountRole } = this.LoggedInUser;

    if (!UserAccountRole) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user permission.',
        { returnError: true }
      );
    }

    const { role } = UserAccountRole;

    if (!role) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user permission role.',
        { returnError: true }
      );
    }

    return new Set(['ADMIN_ADMIN', 'ADMIN_PROJECT_MANAGEMENT_TEAM']).has(role);
  }

  ngOnInit(): void {
    if (!this.hasValidEditPermission()) {
      this.companyForm.disable();
    }
  }
}
